/* Shades hero (sections/shades-hero.liquid).
   One SVG fills the pinned stage, in stage pixels. The frame rests on .sh-slot; each lens
   holds its own copy of the sunset scene. Scroll progress p (0..1 over the track) drives:
     k  the zoom: the frame scales exponentially about the left lens until that lens
        is far bigger than the screen; the lens centre glides to the scene's end spot
        at the rate a camera moving in would give it.
     b  the hand-off: the left lens's scene grows from lens size to filling the stage and
        its palms spread to the screen edges, so the reflection becomes the world.
   Pointer (or tilt, or an idle bob on touch) tips the frame and slides the reflections. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = matchMedia('(max-width:899px),(max-aspect-ratio:4/5)');
  const fine = matchMedia('(pointer:fine)');
  const LC = { x: -258, y: 4 };         // left lens centre, frame units
  const LW = 416, LH = 276;             // left lens glass size, frame units
  const FRAME_W = 1060;                 // frame width incl. hinges, frame units
  const LENS_SCENE = 300;               // scene height (1000 units) maps to this many frame units in a lens

  const clamp = (v, a = 0, b = 1) => v < a ? a : v > b ? b : v;
  const lerp = (a, b, t) => a + (b - a) * t;
  const lerpLog = (a, b, t) => Math.exp(lerp(Math.log(a), Math.log(b), t));
  const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const smooth = t => t * t * (3 - 2 * t);
  const f = n => Math.round(n * 100) / 100;

  /* rise-in words, keeping inline formatting like <em> */
  function splitWords(el) {
    let i = 0;
    const out = document.createDocumentFragment();
    const word = (text, wrapper) => {
      const w = document.createElement('span'), s = document.createElement('span');
      w.className = 'w'; s.style.setProperty('--i', i++);
      if (wrapper) { const c = wrapper.cloneNode(false); c.textContent = text; s.appendChild(c); } else s.textContent = text;
      w.appendChild(s); return w;
    };
    el.childNodes.forEach(n => {
      const text = n.textContent, parts = text.split(/(\s+)/);
      parts.forEach(p => {
        if (!p) return;
        if (/^\s+$/.test(p)) out.appendChild(document.createTextNode(' '));
        else out.appendChild(word(p, n.nodeType === 1 ? n : null));
      });
    });
    el.textContent = '';
    el.appendChild(out);
  }

  function init(root) {
    if (root.__sh) return;
    const stage = root.querySelector('.sh-stage'), svg = root.querySelector('.sh-art'), slot = root.querySelector('.sh-slot');
    const intro = root.querySelector('.sh-intro'), offer = root.querySelector('.sh-offer'), cue = root.querySelector('.sh-cue');
    const title = root.querySelector('.sh-title');
    const frame = svg.querySelector('.sh-frame'), shadow = svg.querySelector('.sh-shadow'), spec = svg.querySelector('.sh-spec');
    const clips = svg.querySelectorAll('.sh-clip');
    const src = svg.querySelector('.sh-scene-src');
    if (!stage || !slot || !src) return;

    const scenes = [...svg.querySelectorAll('.sh-scene')].map(g => {
      const c = src.cloneNode(true);
      c.removeAttribute('style'); c.classList.remove('sh-scene-src');
      g.appendChild(c);
      const parts = {};
      c.querySelectorAll('[data-part]').forEach(el => { parts[el.dataset.part] = el; });
      return { g, c, parts, clip: g.getAttribute('clip-path') };
    });
    src.remove();
    if (title) splitWords(title);

    let W = 0, H = 0, top = 0, F0 = { x: 0, y: 0 }, C = { x: 0, y: 0 }, s0 = 1, s1 = 2, r0 = -6, mob = false, endScale = 1;
    let target = 0, cur = 0, lastP = -1, lastK = -1, trackD = 0, rootY = 0;
    let px = 0, py = 0, tpx = 0, tpy = 0;
    let visible = true, raf = 0, dead = false, still = reduce.matches;
    const born = performance.now();

    function layout() {
      const hdr = document.querySelector('.section-header');
      top = hdr ? hdr.offsetHeight : 0;
      root.style.setProperty('--sh-top', top + 'px');
      root.classList.toggle('is-static', still);
      intro.style.transform = '';
      /* scroll track, measured here so the frame loop reads only scrollY */
      trackD = root.offsetHeight - stage.offsetHeight;
      rootY = root.getBoundingClientRect().top + window.scrollY;
      W = stage.clientWidth; H = stage.clientHeight;
      if (!W || !H) return;
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      mob = narrow.matches;
      const sr = stage.getBoundingClientRect(), r = slot.getBoundingClientRect();
      s0 = r.width / FRAME_W;
      F0 = { x: r.left - sr.left + r.width / 2, y: r.top - sr.top + r.height / 2 - 14 * s0 };
      r0 = mob ? -4 : -7;
      /* where the sunset ends up: sun to the right of the copy on desktop, low under the copy on phones */
      C = mob ? { x: W / 2, y: H * .74 } : { x: W * .68, y: H * .55 };
      endScale = Math.max(H, W * .62) / 1000;
      s1 = 1.9 * Math.max(2 * Math.max(C.x, W - C.x) / LW, 2 * Math.max(C.y, H - C.y) / LH);
      root.style.setProperty('--gx', f(F0.x / W * 100) + '%');
      root.style.setProperty('--gy', f(F0.y / H * 100) + '%');
      lastP = -1;
    }

    function progress() {
      return trackD > 0 ? clamp((top - (rootY - window.scrollY)) / trackD) : 0;
    }

    /* one lens's scene: centre (px), scale (px per unit), visible half-widths left/right (units), depth push 0..1 */
    function placeScene(sc, cx, cy, scale, extL, extR, depth) {
      sc.c.setAttribute('transform', `translate(${f(cx)} ${f(cy)}) scale(${f(scale * 1000) / 1000})`);
      /* palms shrink on narrow screens so they frame the sunset instead of covering it */
      const pf = clamp((extL + extR) / 1500, .5, 1);
      const p = sc.parts, grow = pf * (1 + depth * .28), push = depth * 140 * pf;
      p.palmsL.setAttribute('transform', `translate(${f(-extL + 70 * pf - push)} 560) scale(${f(grow * 1000) / 1000})`);
      p.palmsBack.setAttribute('transform', `translate(${f(-extL + 90 * pf - push * .5)} 560) scale(${f(pf * (1 + depth * .12) * 1000) / 1000})`);
      p.palmsR.setAttribute('transform', `translate(${f(extR - 40 * pf + push)} 590) scale(${f(grow * 1000) / 1000})`);
      p.pier.setAttribute('transform', `translate(${f(extR + 40 + push * .3)} 0)`);
      p.sun.setAttribute('transform', `translate(0 ${f(-depth * 26)})`);
      p.halo.setAttribute('transform', `translate(0 ${f(-depth * 26)})`);
      p.birds.setAttribute('transform', `translate(${f(-depth * 90)} ${f(-depth * 40)})`);
    }

    function render(now) {
      const t = (now - born) / 1000;
      const enter = still ? 1 : 1 - Math.pow(1 - clamp((t - .15) / 1.3), 3);
      const p = still ? 0 : cur;
      const k = ease(clamp((p - .03) / .6));
      const idle = still ? 0 : 1 - clamp(k * 5);
      if (k >= 1 && lastK >= 1 && p === lastP) return;
      lastP = p; lastK = k;

      /* frame */
      const bobY = fine.matches || mob ? 0 : Math.sin(t * 1.25) * 5;
      const bobR = fine.matches || mob ? 0 : Math.sin(t * .8) * .9;
      const sRest = s0 * (.86 + .14 * enter);
      const s = sRest * Math.pow(s1 / sRest, k);
      const u = k <= 0 ? 0 : (1 - sRest / s) / (1 - sRest / s1);
      const rot = r0 * (1 - u) + (px * 4 + bobR) * idle;
      const rad = rot * Math.PI / 180, cs = Math.cos(rad), sn = Math.sin(rad);
      const rotPt = (x, y, sc) => ({ x: (x * cs - y * sn) * sc, y: (x * sn + y * cs) * sc });
      const lRest = rotPt(LC.x, LC.y, sRest);
      const L0 = { x: F0.x + lRest.x + px * 14 * idle, y: F0.y + lRest.y + (py * 10 + bobY + (1 - enter) * 40) * idle };
      const L = { x: lerp(L0.x, C.x, u), y: lerp(L0.y, C.y, u) };
      const lv = rotPt(LC.x, LC.y, s);
      const Fx = L.x - lv.x, Fy = L.y - lv.y;
      const tf = `translate(${f(Fx)} ${f(Fy)}) rotate(${f(rot)}) scale(${f(s * 10000) / 10000})`;

      const done = k >= .999;
      frame.style.display = done ? 'none' : '';
      if (!done) {
        frame.setAttribute('transform', tf);
        frame.style.opacity = enter;
        shadow.style.opacity = clamp(1 - k * 4);
        spec.setAttribute('transform', `translate(${f(px * 70 * idle)} 0)`);
        spec.style.opacity = clamp(1 - k * 6);
        clips[0].setAttribute('transform', tf);
        clips[1].setAttribute('transform', tf + ' scale(-1 1)');
      }

      /* reflections drift against the tilt, like a mirror coating */
      const drift = fine.matches || mob ? 0 : Math.sin(t * .45) * 70;
      const rx = (-px * 130 + drift) * idle, ry = -py * 50 * idle;

      /* left lens: becomes the stage */
      const b = smooth(clamp((k - .28) / .72));
      const sl = scenes[0], lensScale = s * LENS_SCENE / 1000;
      const scale = lerpLog(lensScale, endScale, b);
      const lensExt = LW / 2 / (LENS_SCENE / 1000);
      const extL = lerpLog(lensExt, C.x / endScale, b), extR = lerpLog(lensExt, (W - C.x) / endScale, b);
      const depth = smooth(clamp((p - .5) / .5));
      placeScene(sl, lerp(L.x + rx * lensScale, C.x, b), lerp(L.y + ry * lensScale, C.y, b), scale, extL, extR, depth);
      if (done) sl.g.removeAttribute('clip-path'); else sl.g.setAttribute('clip-path', sl.clip);
      sl.c.style.opacity = enter;

      /* right lens: rides along until it leaves the screen */
      const sr = scenes[1];
      sr.g.style.display = done ? 'none' : '';
      if (!done) {
        const rv = rotPt(-LC.x, LC.y, s);
        placeScene(sr, Fx + rv.x + (rx + 60) * lensScale, Fy + rv.y + ry * lensScale, lensScale, lensExt, lensExt, 0);
        sr.c.style.opacity = enter;
      }

      /* copy */
      const io = clamp(1 - p / .16);
      intro.style.opacity = io;
      intro.style.transform = io < 1 ? `translateY(${f(-(1 - io) * 50)}px)` : '';
      intro.style.visibility = io <= 0 ? 'hidden' : '';
      const q = clamp((p - .64) / .16), qe = 1 - Math.pow(1 - q, 3);
      offer.classList.toggle('is-on', q > 0);
      offer.style.opacity = still ? '' : qe;
      offer.style.transform = still ? '' : `translateY(${f((1 - qe) * 36)}px)`;
      if (cue) cue.style.opacity = clamp(1 - p / .04);
    }

    function frameLoop(now) {
      raf = 0;
      if (dead) return;
      target = progress();
      cur += (target - cur) * .16;
      if (Math.abs(target - cur) < .0004) cur = target;
      px += (tpx - px) * .07; py += (tpy - py) * .07;
      render(now);
      /* phones have no idle bob or tilt, so once the entrance is over and the scroll has
         settled the loop sleeps; the next scroll event kicks it again */
      const settled = mob && cur === target && now - born > 1600;
      if (visible && !still && !settled) raf = requestAnimationFrame(frameLoop);
    }
    const kick = () => { if (!raf && !dead && visible && !still) raf = requestAnimationFrame(frameLoop); };

    function relayout() {
      still = reduce.matches;
      layout();
      cur = target = still ? 0 : progress();
      render(performance.now());
      kick();
    }

    const onMove = e => {
      if (!fine.matches || still) return;
      const r = stage.getBoundingClientRect();
      tpx = clamp(((e.clientX - r.left) / r.width - .5) * 2, -1, 1);
      tpy = clamp(((e.clientY - r.top) / r.height - .5) * 2, -1, 1);
    };
    const onLeave = () => { tpx = tpy = 0; };
    const onTilt = e => {
      if (e.gamma == null || fine.matches || mob) return;
      tpx = clamp(e.gamma / 25, -1, 1); tpy = clamp((e.beta - 50) / 30, -1, 1);
    };
    let rz = 0;
    const onResize = () => { clearTimeout(rz); rz = setTimeout(relayout, 120); };

    stage.addEventListener('mousemove', onMove);
    stage.addEventListener('mouseleave', onLeave);
    if (window.DeviceOrientationEvent && typeof DeviceOrientationEvent.requestPermission !== 'function') window.addEventListener('deviceorientation', onTilt);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', kick, { passive: true });
    reduce.addEventListener && reduce.addEventListener('change', relayout);
    const vis = 'IntersectionObserver' in window ? new IntersectionObserver(es => { visible = es[0].isIntersecting; kick(); }) : null;
    if (vis) vis.observe(root);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);
    window.addEventListener('load', relayout, { once: true });

    root.__sh = () => {
      dead = true; cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize); window.removeEventListener('scroll', kick);
      window.removeEventListener('deviceorientation', onTilt);
      vis && vis.disconnect();
    };

    relayout();
    svg.classList.add('is-ready');
    requestAnimationFrame(() => root.classList.add('is-in'));
  }

  const boot = () => document.querySelectorAll('[data-shades-hero]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('[data-shades-hero]').forEach(init));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-shades-hero]').forEach(r => { r.__sh && r.__sh(); }));
})();
